// terminal/src/components/FileExplorer/fileIcons.ts

import type { FileEntry } from './types';

/**
 * Get icon for a file entry based on type and extension
 */
export function getFileIcon(entry: FileEntry): string {
  // Directories
  if (entry.entry_type === 'directory') {
    return '📁';
  }

  // Symlinks
  if (entry.symlink) {
    return '🔗';
  }

  // Get extension
  const ext = entry.name.split('.').pop()?.toLowerCase() || '';

  // Special filenames
  const specialFiles: Record<string, string> = {
    'dockerfile': '🐳',
    'docker-compose.yml': '🐳',
    'docker-compose.yaml': '🐳',
    'makefile': '⚙️',
    'cmakelists.txt': '⚙️',
    'cargo.toml': '🦀',
    'cargo.lock': '🔒',
    'package.json': '📦',
    'package-lock.json': '🔒',
    'yarn.lock': '🔒',
    'pnpm-lock.yaml': '🔒',
    'tsconfig.json': '📘',
    'jsconfig.json': '📒',
    '.gitignore': '🙈',
    '.gitattributes': '🙈',
    '.env': '🔐',
    '.env.local': '🔐',
    '.env.example': '🔐',
    'license': '📜',
    'license.md': '📜',
    'license.txt': '📜',
    'readme.md': '📖',
    'readme': '📖',
    'changelog.md': '📋',
    'contributing.md': '🤝',
  };

  const lowerName = entry.name.toLowerCase();
  if (specialFiles[lowerName]) {
    return specialFiles[lowerName];
  }

  // Extension-based icons
  const iconMap: Record<string, string> = {
    // TypeScript/JavaScript
    'ts': '📘',
    'tsx': '📘',
    'mts': '📘',
    'cts': '📘',
    'js': '📒',
    'jsx': '📒',
    'mjs': '📒',
    'cjs': '📒',

    // Rust
    'rs': '🦀',

    // Python
    'py': '🐍',
    'pyi': '🐍',
    'pyc': '🐍',
    'pyw': '🐍',

    // Go
    'go': '🐹',
    'mod': '🐹',
    'sum': '🔒',

    // Ruby
    'rb': '💎',
    'erb': '💎',
    'rake': '💎',

    // Java/Kotlin
    'java': '☕',
    'kt': '🟣',
    'kts': '🟣',
    'jar': '☕',

    // C/C++
    'c': '🔷',
    'h': '🔷',
    'cpp': '🔷',
    'hpp': '🔷',
    'cc': '🔷',
    'cxx': '🔷',

    // C#
    'cs': '🟢',
    'csx': '🟢',

    // PHP
    'php': '🐘',

    // Swift
    'swift': '🍎',

    // Shell
    'sh': '⚙️',
    'bash': '⚙️',
    'zsh': '⚙️',
    'fish': '⚙️',
    'ps1': '⚙️',
    'bat': '⚙️',
    'cmd': '⚙️',

    // Config
    'json': '📋',
    'jsonc': '📋',
    'json5': '📋',
    'yaml': '📋',
    'yml': '📋',
    'toml': '📋',
    'xml': '📋',
    'ini': '📋',
    'conf': '📋',
    'config': '📋',
    'properties': '📋',

    // Markup
    'html': '🌐',
    'htm': '🌐',
    'xhtml': '🌐',
    'vue': '💚',
    'svelte': '🧡',

    // Styles
    'css': '🎨',
    'scss': '🎨',
    'sass': '🎨',
    'less': '🎨',
    'styl': '🎨',

    // Documentation
    'md': '📝',
    'mdx': '📝',
    'markdown': '📝',
    'txt': '📄',
    'rst': '📄',
    'adoc': '📄',

    // Data
    'csv': '📊',
    'tsv': '📊',
    'sql': '🗃️',
    'db': '🗃️',
    'sqlite': '🗃️',

    // Images
    'png': '🖼️',
    'jpg': '🖼️',
    'jpeg': '🖼️',
    'gif': '🖼️',
    'webp': '🖼️',
    'svg': '🖼️',
    'ico': '🖼️',
    'bmp': '🖼️',
    'tiff': '🖼️',

    // Audio/Video
    'mp3': '🎵',
    'wav': '🎵',
    'ogg': '🎵',
    'flac': '🎵',
    'mp4': '🎬',
    'webm': '🎬',
    'mkv': '🎬',
    'avi': '🎬',
    'mov': '🎬',

    // Archives
    'zip': '📦',
    'tar': '📦',
    'gz': '📦',
    'bz2': '📦',
    'xz': '📦',
    'rar': '📦',
    '7z': '📦',

    // Documents
    'pdf': '📕',
    'doc': '📘',
    'docx': '📘',
    'xls': '📗',
    'xlsx': '📗',
    'ppt': '📙',
    'pptx': '📙',

    // Fonts
    'ttf': '🔤',
    'otf': '🔤',
    'woff': '🔤',
    'woff2': '🔤',
    'eot': '🔤',

    // Certificates/Keys
    'pem': '🔑',
    'crt': '🔑',
    'key': '🔑',
    'cer': '🔑',
    'p12': '🔑',

    // Logs
    'log': '📜',

    // Executables
    'exe': '⚡',
    'dll': '⚡',
    'so': '⚡',
    'dylib': '⚡',
    'bin': '⚡',
  };

  return iconMap[ext] || '📄';
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number | null): string {
  if (bytes === null || bytes === undefined) {
    return '-';
  }

  if (bytes === 0) return '0 B';

  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  const size = bytes / Math.pow(k, i);

  return `${size.toFixed(i > 0 ? 1 : 0)} ${units[i]}`;
}

/**
 * Format timestamp for display
 */
export function formatModified(timestamp: number | null): string {
  if (timestamp === null || timestamp === undefined) {
    return '-';
  }

  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  // Today
  if (diffDays === 0) {
    return date.toLocaleTimeString(undefined, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  // Yesterday
  if (diffDays === 1) {
    return 'Yesterday';
  }

  // Within a week
  if (diffDays < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }

  // This year
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  }

  // Older
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
